import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FolderKanban, Plus, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { ProjectStatus, ProjectType } from '@bizsolutions/shared';

interface Project {
  id: string;
  projectNumber: number;
  name: string;
  accountName: string | null;
  projectType: string;
  status: string;
  completionPct: number;
  totalLoggedHours: number;
  budgetAmount: number | null;
}

const statusVariant: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary' | 'info'> = {
  planning: 'secondary',
  active: 'success',
  on_hold: 'warning',
  completed: 'info',
  cancelled: 'destructive',
  archived: 'secondary',
};

const projectTypeLabels: Record<string, string> = {
  consulting: 'Consulting',
  software_design: 'Software Design',
  software_dev: 'Software Dev',
  staff_augmentation: 'Staff Aug',
  fixed_price: 'Fixed Price',
  retainer: 'Retainer',
  internal: 'Internal',
};

export function ProjectsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['projects', { page, search, status: statusFilter, projectType: typeFilter }],
    queryFn: async () => {
      const result = await api.listProjects({
        page,
        limit: 20,
        search: search || undefined,
        status: statusFilter || undefined,
        projectType: typeFilter || undefined,
      });
      return result;
    },
  });

  const projects = (data?.data || []) as Project[];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Projects</h1>
          <p className="text-muted-foreground">Manage and track all your projects</p>
        </div>
        <Button onClick={() => navigate('/projects/list/new')}>
          <Plus className="h-4 w-4 mr-2" />
          New Project
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search projects..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-10"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(value) => {
            setStatusFilter(value === 'all' ? '' : value);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value={ProjectStatus.PLANNING}>Planning</SelectItem>
            <SelectItem value={ProjectStatus.ACTIVE}>Active</SelectItem>
            <SelectItem value={ProjectStatus.ON_HOLD}>On Hold</SelectItem>
            <SelectItem value={ProjectStatus.COMPLETED}>Completed</SelectItem>
            <SelectItem value={ProjectStatus.CANCELLED}>Cancelled</SelectItem>
            <SelectItem value={ProjectStatus.ARCHIVED}>Archived</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={typeFilter}
          onValueChange={(value) => {
            setTypeFilter(value === 'all' ? '' : value);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value={ProjectType.CONSULTING}>Consulting</SelectItem>
            <SelectItem value={ProjectType.SOFTWARE_DESIGN}>Software Design</SelectItem>
            <SelectItem value={ProjectType.SOFTWARE_DEV}>Software Dev</SelectItem>
            <SelectItem value={ProjectType.STAFF_AUGMENTATION}>Staff Aug</SelectItem>
            <SelectItem value={ProjectType.FIXED_PRICE}>Fixed Price</SelectItem>
            <SelectItem value={ProjectType.RETAINER}>Retainer</SelectItem>
            <SelectItem value={ProjectType.INTERNAL}>Internal</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : projects.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FolderKanban className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-1">No projects found</h3>
            <p className="text-muted-foreground mb-4">
              {search || statusFilter || typeFilter
                ? 'Try adjusting your filters'
                : 'Create your first project to get started'}
            </p>
            {!search && !statusFilter && !typeFilter && (
              <Button onClick={() => navigate('/projects/list/new')}>
                <Plus className="h-4 w-4 mr-2" />
                Create Project
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 text-sm font-medium">#</th>
                  <th className="text-left p-3 text-sm font-medium">Name</th>
                  <th className="text-left p-3 text-sm font-medium">Client</th>
                  <th className="text-left p-3 text-sm font-medium">Type</th>
                  <th className="text-left p-3 text-sm font-medium">Status</th>
                  <th className="text-left p-3 text-sm font-medium">Progress</th>
                  <th className="text-right p-3 text-sm font-medium">Hours</th>
                  <th className="text-right p-3 text-sm font-medium">Budget</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((project) => (
                  <tr
                    key={project.id}
                    className="border-b hover:bg-muted/30 cursor-pointer"
                    onClick={() => navigate(`/projects/list/${project.id}`)}
                  >
                    <td className="p-3 text-sm text-muted-foreground">{project.projectNumber}</td>
                    <td className="p-3 text-sm font-medium">{project.name}</td>
                    <td className="p-3 text-sm">{project.accountName || 'Internal'}</td>
                    <td className="p-3 text-sm">
                      {projectTypeLabels[project.projectType] || project.projectType}
                    </td>
                    <td className="p-3">
                      <Badge variant={statusVariant[project.status] || 'secondary'}>
                        {project.status.replace('_', ' ')}
                      </Badge>
                    </td>
                    <td className="p-3 text-sm">{project.completionPct.toFixed(0)}%</td>
                    <td className="p-3 text-sm text-right">{project.totalLoggedHours.toFixed(1)}h</td>
                    <td className="p-3 text-sm text-right">
                      {project.budgetAmount != null ? formatCurrency(project.budgetAmount) : '--'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * 20 + 1} to {Math.min(page * 20, pagination.total)} of{' '}
            {pagination.total} projects
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p - 1)}
              disabled={page <= 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">
              Page {page} of {pagination.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= pagination.totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
